import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatMessage from '../../components/ChatMessage';
import ChatInput from '../../components/ChatInput';
import { getConversation, sendMessage } from '../../services/api';
import { useMRN } from '../../context/MRNContext';
import { usePatientRealtime } from '../../services/usePatientRealTime';
import '../../App.css';

// Attach a stable client-side id to any synthetic message object (greeting, errors, etc.)
// so the Realtime duplicate-check never compares undefined === undefined.
function syntheticMessage(fields) {
  return { ...fields, id: crypto.randomUUID() };
}

function Chat() {
  const navigate = useNavigate();
  const { mrn } = useMRN(); // Custom hook to access MRN from context
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [incomingTranscript, setIncomingTranscript] = useState('');
  const messagesEndRef = useRef(null);

  // Redirect to welcome if MRN is not set.

  useEffect(() => {
    if (!mrn) {
      navigate('/');
    }
  }, [mrn, navigate]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // =========================================================================
  // SUPABASE REALTIME LISTENER ENGINE
  // =========================================================================
  // The listener hook now runs persistently (gated with `true`). As your server writes 
  // live text fragments into your 'chat_transcripts' table, this hook picks up the insert 
  // broadcast and reflects it instantly inside the UI textarea.
  usePatientRealtime(mrn, true, (newRow) => {
    // Map this property exactly to the column your backend server writes to
    if (newRow?.text) {
      setIncomingTranscript(newRow.text);
    }
  });

  // ─── 401 handler ─────────────────────────────────────────────────────────
  const buildUnauthorizedExit = useCallback(
    () => {
      setMessages([
        syntheticMessage({
          role: 'assistant',
          message: 'Thank you for sharing your information.',
        }),
      ]);
      const timerId = setTimeout(() => navigate('/'), 10000);
      return timerId;
    },
    [navigate]
  );

  // ─── Load conversation on mount ───────────────────────────────────────────
  useEffect(() => {
    if (!mrn) return;

    let timerId = null;
    let cancelled = false;

    const loadConversation = async () => {
      setLoading(true);
      try {
        const data = await getConversation(mrn);
        if (!cancelled) setMessages(data.conversation);
      } catch (error) {
        if (cancelled) return;
        if (
          error.response?.status === 401 &&
          error.response.data?.code === 'UNAUTHORIZED_ASYMMETRIC_JWT'
        ) {
          timerId = buildUnauthorizedExit();
        } else {
          setMessages([
            syntheticMessage({
              role: 'assistant',
              message:
                "Hi! I'm here to help prepare for your doctor's appointment. What brings you in today?",
            }),
          ]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadConversation();

    return () => {
      cancelled = true;
      if (timerId != null) clearTimeout(timerId);
    };
  }, [mrn, buildUnauthorizedExit]);

  // ─── Send a typed message ─────────────────────────────────────────────────
  const handleSendMessage = async (messageText) => {
    let timerId = null;

    // Optimistically add the user message.
    const userMessage = syntheticMessage({ role: 'user', message: messageText });
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
      const data = await sendMessage(mrn, messageText);
      setMessages(data.conversation);
    } catch (error) {
      console.error('Error sending message:', error);
      if (
        error.response?.status === 401 &&
        error.response.data?.code === 'UNAUTHORIZED_ASYMMETRIC_JWT'
      ) {
        timerId = buildUnauthorizedExit();
      } else {
        setMessages((prev) => [
          ...prev,
          syntheticMessage({
            role: 'assistant',
            message: 'Sorry, something went wrong. Please try again.',
          }),
        ]);
      }
    } finally {
      setLoading(false);
    }

    return timerId;
  };

  if (!mrn) return null;

  return (
    <div className="chat-container">
      <div className="header1">
        <img src="/images/icon.png" alt="Logo" className="auth-logo" />
        <h1>Pre-Visit Health Chat</h1>
        <p>Share what you feel matters about your health, in your own way. Every detail helps guide your care.</p>
        <p>MRN: {mrn}</p>
      </div>

      <div className="messages">
        {messages.map((msg, index) => (
          <ChatMessage key={index} role={msg.role} message={msg.message} />
        ))}

        <div ref={messagesEndRef} />
      </div>

      <ChatInput
        onSendMessage={handleSendMessage}
        disabled={loading}
        incomingTranscript={incomingTranscript}
        onTranscriptConsumed={() => setIncomingTranscript('')}
        mrn={mrn}
      />
    </div>
  );
}

export default Chat;